import { useCheckAccountMutation } from "../store/api/usersApiSlice";
import { buildCheckAccountPayload } from "../utils/doctorAuthHelpers";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

/**
 * Custom hook for checking account availability (email and phone)
 * @returns {Function} checkAccountAvailability - Function to check if email/phone is available
 */
const useAccountCheck = () => {
  const [checkAccount] = useCheckAccountMutation();
  const { t } = useTranslation();

  /**
   * Checks if email and phone number are available
   * @param {Object} params - Parameters object
   * @param {string} params.email - Email address to check
   * @param {string} params.phone - Phone number to check (full format with country code, e.g., "+905551234567")
   * @param {Object} [params.formikHelpers] - Formik helpers object (optional, for setting field errors)
   * @param {Function} [params.setLoading] - Loading state setter function (optional)
   * @param {boolean} [params.checkEmail=true] - Whether to check email availability (default: true)
   * @param {boolean} [params.checkPhone=true] - Whether to check phone availability (default: true)
   * @returns {Promise<{isValid: boolean, data: Object|null}>} - Object with isValid flag and response data
   */
  const checkAccountAvailability = async ({
    email,
    phone,
    formikHelpers = null,
    setLoading = null,
    checkEmail = true,
    checkPhone = true,
  }) => {
    try {
      const checkResult = await checkAccount(
        buildCheckAccountPayload({ email: email || "", phone: phone || "" })
      ).unwrap();

      if (!checkResult?.isSuccess) {
        const errorMsg = checkResult?.error?.messages
          ? Object.values(checkResult.error.messages)[0]
          : null;
        if (errorMsg) {
          // TODO: hata mesajı geri açılacaktır.
          // toast.error(errorMsg);
          toast.error(t("error_generic_action_failed"));
        } else toast.error(t("error_generic_action_failed"));
        if (setLoading) setLoading(false);
        return { isValid: false, data: null };
      }

      const response = checkResult.data;

      // Email ve telefon kullanılabilir mi kontrol et
      const isEmailUsable = response?.isEmailUsable ?? response?.IsEmailUsable;
      const isPhoneUsable = response?.isPhoneUsable ?? response?.IsPhoneUsable;

      // Email kontrolü
      if (checkEmail && response && isEmailUsable === false) {
        if (formikHelpers) {
          formikHelpers.setFieldTouched("email", true, true);
          // TODO: hata mesajı geri açılacaktır.
          // formikHelpers.setFieldError("email", response?.emailMessage?.value || response?.EmailMessage?.Value || "Bu email adresi zaten kullanımda.");
          formikHelpers.setFieldError("email", t("error_generic_register_conflict_with_signin_hint"));
        }
        // TODO: hata mesajı geri açılacaktır.
        // toast.error(response?.emailMessage?.value || response?.EmailMessage?.Value || "Bu email adresi zaten kullanımda.");
        toast.error(t("error_generic_register_conflict_with_signin_hint"));
        if (setLoading) setLoading(false);
        return { isValid: false, data: response };
      }

      // Telefon kontrolü
      if (checkPhone && response && isPhoneUsable === false) {
        if (formikHelpers) {
          formikHelpers.setFieldTouched("phoneNumber", true, true);
          // TODO: hata mesajı geri açılacaktır.
          // formikHelpers.setFieldError("phoneNumber", response?.phoneMessage?.value || response?.PhoneMessage?.Value || "Bu telefon numarası zaten kullanımda.");
          formikHelpers.setFieldError("phoneNumber", t("error_generic_register_conflict_with_signin_hint"));
        }
        // TODO: hata mesajı geri açılacaktır.
        // toast.error(response?.phoneMessage?.value || response?.PhoneMessage?.Value || "Bu telefon numarası zaten kullanımda.");
        toast.error(t("error_generic_register_conflict_with_signin_hint"));
        if (setLoading) setLoading(false);
        return { isValid: false, data: response };
      }

      // Her şey geçerli
      return { isValid: true, data: response };
    } catch (checkErr) {
      console.error("Hesap kontrolü hatası:", checkErr);
      const errorMsg = checkErr?.data?.error?.messages
        ? Object.values(checkErr.data.error.messages)[0]
        : null;
      if (errorMsg) {
        // TODO: hata mesajı geri açılacaktır.
        // toast.error(errorMsg);
        toast.error(t("error_generic_action_failed"));
      } else toast.error(t("error_generic_action_failed"));
      if (setLoading) setLoading(false);
      return { isValid: false, data: null };
    }
  };

  return { checkAccountAvailability };
};

export default useAccountCheck;

